import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { useEstimation } from '@/hooks/useEstimation';
import { Product } from '@/types/ProductType';

interface ProductDetailMonthlyPaymentModalProps {
  open: boolean;
  onClose: () => void;
  product: Product;
  comparison: {
    loanProgram: USER_LOAN_PROGRAM;
    downPayment: number;
    bridgeTermMonths?: number;
  };
  onSave: (values: {
    monthlyPropertyTaxes?: number;
    monthlyHomeownersInsurance?: number;
    hoaFees?: number;
  }) => void;
  onReset?: () => void;
}

export const ProductDetailMonthlyPaymentModal = ({
  open,
  onClose,
  product,
  comparison,
  onSave,
  onReset,
}: ProductDetailMonthlyPaymentModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Create modified product for estimation
  const modifiedProduct = {
    ...product,
    user: {
      ...product.user,
      loanProgram: comparison.loanProgram,
      loanInfo: {
        ...product.user?.loanInfo,
        downPayment: comparison.downPayment,
      },
    },
  };
  const { estimation } = useEstimation(
    modifiedProduct as any,
    comparison.downPayment,
    comparison.loanProgram,
    comparison.loanProgram === USER_LOAN_PROGRAM.BRIDGE
      ? comparison.bridgeTermMonths ?? 12
      : undefined
  );

  const [monthlyPropertyTaxes, setMonthlyPropertyTaxes] = useState('0');
  const [monthlyHomeownersInsurance, setMonthlyHomeownersInsurance] =
    useState('0');
  const [hoaFees, setHoaFees] = useState('0');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (estimation && open) {
      setMonthlyPropertyTaxes(
        ((estimation as any)?.monthlyPropertyTaxes || 0).toString()
      );
      setMonthlyHomeownersInsurance(
        ((estimation as any)?.monthlyHomeownersInsurance || 0).toString()
      );
      setHoaFees(((estimation as any)?.hoaFees || 0).toString());
      setErrors({});
    }
  }, [estimation, open]);

  const handleInputChange = (field: string, value: string) => {
    const numValue = parseFloat(value);

    if (value === '' || (!isNaN(numValue) && numValue >= 0)) {
      switch (field) {
        case 'propertyTaxes':
          setMonthlyPropertyTaxes(value);
          break;
        case 'homeownersInsurance':
          setMonthlyHomeownersInsurance(value);
          break;
        case 'hoaFees':
          setHoaFees(value);
          break;
      }

      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    } else {
      setErrors((prev) => ({
        ...prev,
        [field]: 'Please enter a valid number',
      }));
    }
  };

  const handleSave = () => {
    const propertyTaxesNum = parseFloat(monthlyPropertyTaxes) || 0;
    const homeownersInsuranceNum = parseFloat(monthlyHomeownersInsurance) || 0;
    const hoaFeesNum = parseFloat(hoaFees) || 0;

    if (propertyTaxesNum < 0 || homeownersInsuranceNum < 0 || hoaFeesNum < 0) {
      setErrors({
        propertyTaxes: propertyTaxesNum < 0 ? 'Cannot be negative' : '',
        homeownersInsurance:
          homeownersInsuranceNum < 0 ? 'Cannot be negative' : '',
        hoaFees: hoaFeesNum < 0 ? 'Cannot be negative' : '',
      });
      return;
    }

    onSave({
      monthlyPropertyTaxes: propertyTaxesNum,
      monthlyHomeownersInsurance: homeownersInsuranceNum,
      hoaFees: hoaFeesNum,
    });
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent, field: string) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  // Calculate PITIA breakdown for display
  const monthlyPrincipalInterest = estimation
    ? estimation.type === USER_LOAN_PROGRAM.FIX_AND_FLIP
      ? (estimation as any).interestPayment || 0
      : (estimation as any).principalAndInterest || 0
    : 0;

  const monthlyPrivateMortgageInsurance =
    estimation && estimation.type === USER_LOAN_PROGRAM.DEFAULT
      ? (estimation as any).monthlyprivateMortgageInsuranceBase || 0
      : 0;

  const monthlyMortgageInsurance = estimation
    ? estimation.type === USER_LOAN_PROGRAM.FHA
      ? (estimation as any).monthlyMIP || 0
      : estimation.type === USER_LOAN_PROGRAM.CONVENTIONAL
      ? (estimation as any).monthlyPMIPayment || 0
      : 0
    : 0;

  const monthlyUSDAFee =
    estimation && estimation.type === USER_LOAN_PROGRAM.USDA
      ? (estimation as any).monthlyGuaranteeFee || 0
      : 0;

  const totalMortgageInsurance =
    monthlyPrivateMortgageInsurance + monthlyMortgageInsurance + monthlyUSDAFee;
  const currentTotal =
    parseFloat(monthlyPropertyTaxes || '0') +
    parseFloat(monthlyHomeownersInsurance || '0') +
    parseFloat(hoaFees || '0') +
    monthlyPrincipalInterest +
    totalMortgageInsurance;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          background: theme.palette.primary.main,
          border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          borderRadius: isMobile ? 0 : 3,
          boxShadow: theme.shadows[24],
          height: isMobile ? 'auto' : 'auto',
          maxHeight: isMobile ? '100vh' : '90vh',
          width: '100%',
          maxWidth: isMobile ? '100%' : 'min(640px, 95vw)',
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.common.white,
            0.1
          )} 0%, ${alpha(theme.palette.common.white, 0.05)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          p: isMobile ? 2 : 3,
        }}
      >
        <Typography
          variant={isMobile ? 'h5' : 'h6'}
          fontWeight={700}
          color="white"
          sx={{ flex: 1 }}
        >
          Monthly Payment (PITIA) Configuration
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: isMobile ? 2 : 3,
          overflowY: 'auto',
          maxHeight: isMobile ? 'none' : '65vh',
        }}
      >
        {/* Current Total Display */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.common.white,
              0.15
            )} 0%, ${alpha(theme.palette.common.white, 0.05)} 100%)`,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="white"
            gutterBottom
          >
            Total Monthly Payment (PITIA)
          </Typography>
          <Typography
            variant="h4"
            fontWeight={800}
            color="white"
            sx={{
              textShadow: `0 2px 4px ${alpha(theme.palette.common.white, 0.3)}`,
            }}
          >
            ${currentTotal.toLocaleString()}
          </Typography>
        </Box>

        {/* Editable Fields */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={700} color="white" gutterBottom>
            Edit Monthly Costs
          </Typography>

          {/* Property Taxes */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="white"
              gutterBottom
            >
              Monthly Property Taxes
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={monthlyPropertyTaxes}
              onChange={(e) =>
                handleInputChange('propertyTaxes', e.target.value)
              }
              onKeyPress={(e) => handleKeyPress(e, 'propertyTaxes')}
              placeholder="Enter monthly property taxes"
              error={!!errors.propertyTaxes}
              helperText={errors.propertyTaxes}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.common.white,
                    0.15
                  )} 0%, ${alpha(theme.palette.common.white, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                  borderRadius: 2,
                  color: 'white',
                  '&:hover': {
                    borderColor: alpha(theme.palette.common.white, 0.5),
                  },
                  '&.Mui-focused': {
                    borderColor: 'white',
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.common.white,
                      0.2
                    )}`,
                  },
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  padding: isMobile ? 2 : 2.5,
                },
                '& .MuiFormHelperText-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
              InputProps={{
                startAdornment: (
                  <Typography
                    variant="body1"
                    color="white"
                    sx={{ mr: 1, fontWeight: 600 }}
                  >
                    $
                  </Typography>
                ),
              }}
            />
          </Box>

          {/* Homeowners Insurance */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="white"
              gutterBottom
            >
              Monthly Homeowners Insurance
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={monthlyHomeownersInsurance}
              onChange={(e) =>
                handleInputChange('homeownersInsurance', e.target.value)
              }
              onKeyPress={(e) => handleKeyPress(e, 'homeownersInsurance')}
              placeholder="Enter monthly homeowners insurance"
              error={!!errors.homeownersInsurance}
              helperText={errors.homeownersInsurance}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.common.white,
                    0.15
                  )} 0%, ${alpha(theme.palette.common.white, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                  borderRadius: 2,
                  color: 'white',
                  '&:hover': {
                    borderColor: alpha(theme.palette.common.white, 0.5),
                  },
                  '&.Mui-focused': {
                    borderColor: 'white',
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.common.white,
                      0.2
                    )}`,
                  },
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  padding: isMobile ? 2 : 2.5,
                },
                '& .MuiFormHelperText-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
              InputProps={{
                startAdornment: (
                  <Typography
                    variant="body1"
                    color="white"
                    sx={{ mr: 1, fontWeight: 600 }}
                  >
                    $
                  </Typography>
                ),
              }}
            />
          </Box>

          {/* HOA Fees */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="white"
              gutterBottom
            >
              Monthly HOA Fees
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={hoaFees}
              onChange={(e) => handleInputChange('hoaFees', e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'hoaFees')}
              placeholder="Enter monthly HOA fees"
              error={!!errors.hoaFees}
              helperText={errors.hoaFees}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.common.white,
                    0.15
                  )} 0%, ${alpha(theme.palette.common.white, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                  borderRadius: 2,
                  color: 'white',
                  '&:hover': {
                    borderColor: alpha(theme.palette.common.white, 0.5),
                  },
                  '&.Mui-focused': {
                    borderColor: 'white',
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.common.white,
                      0.2
                    )}`,
                  },
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  padding: isMobile ? 2 : 2.5,
                },
                '& .MuiFormHelperText-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
              InputProps={{
                startAdornment: (
                  <Typography
                    variant="body1"
                    color="white"
                    sx={{ mr: 1, fontWeight: 600 }}
                  >
                    $
                  </Typography>
                ),
              }}
            />
          </Box>
        </Box>

        <Divider
          sx={{ my: 3, borderColor: alpha(theme.palette.common.white, 0.2) }}
        />

        {/* Breakdown Summary */}
        <Box>
          <Box
            sx={{
              p: 2,
              background: alpha(theme.palette.common.white, 0.05),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
            }}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                Principal & Interest
              </Typography>
              <Typography variant="body2" fontWeight={600} color="white">
                ${monthlyPrincipalInterest.toLocaleString()}
              </Typography>
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                Property Taxes
              </Typography>
              <Typography variant="body2" fontWeight={600} color="white">
                ${(parseFloat(monthlyPropertyTaxes) || 0).toLocaleString()}
              </Typography>
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                Homeowners Insurance
              </Typography>
              <Typography variant="body2" fontWeight={600} color="white">
                $
                {(parseFloat(monthlyHomeownersInsurance) || 0).toLocaleString()}
              </Typography>
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                HOA Fees
              </Typography>
              <Typography variant="body2" fontWeight={600} color="white">
                ${(parseFloat(hoaFees) || 0).toLocaleString()}
              </Typography>
            </Box>
            {totalMortgageInsurance > 0 && (
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
              >
                <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                  Mortgage Insurance
                </Typography>
                <Typography variant="body2" fontWeight={600} color="white">
                  ${totalMortgageInsurance.toLocaleString()}
                </Typography>
              </Box>
            )}
            <Divider
              sx={{
                my: 1.5,
                borderColor: alpha(theme.palette.common.white, 0.2),
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" fontWeight={700} color="white">
                Total (PITIA)
              </Typography>
              <Typography variant="h6" fontWeight={800} color="white">
                ${currentTotal.toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>

        {Object.keys(errors).length > 0 && (
          <Alert
            severity="error"
            sx={{
              mt: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.error.main,
                0.2
              )} 0%, ${alpha(theme.palette.error.main, 0.1)} 100%)`,
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
              '& .MuiAlert-message': {
                color: 'white',
              },
            }}
          >
            Please fix the errors above before saving.
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: isMobile ? 2 : 3,
          background: theme.palette.primary.main,
          borderTop: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 1,
        }}
      >
        <Button
          onClick={() => {
            onReset?.();
            onClose();
          }}
          variant="outlined"
          fullWidth={isMobile}
          disabled={!onReset}
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.35)',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          Revert to Calculated
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth={isMobile}
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          disabled={Object.keys(errors).length > 0}
          fullWidth={isMobile}
          sx={{
            background: 'white',
            color: theme.palette.primary.main,
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.9)',
            },
            '&.Mui-disabled': {
              background: 'rgba(255, 255, 255, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
